import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { registerFonts } from './fontRegistry';
import { ModernDualColumnPDF } from './ModernDualColumnPDF';
import { DataDrivenPDF } from './DataDrivenPDF';
import { TimelinePDF } from './TimelinePDF';

import { getResponsibilityBullets, formatProjectMetadata, formatContactLink } from '../../../utils/resumeExport';

// Register fonts before rendering
registerFonts();

const cleanStr = (s) => {
  if (s === null || s === undefined) return '';
  if (typeof s === 'string') return s.trim();
  if (typeof s === 'number' || typeof s === 'boolean') return String(s);
  if (typeof s === 'object') {
    const extracted = s.text || s.description || s.label || s.title || s.name || s.value || s.language || s.proficiency || s.company || s.role || s.degree || s.project || '';
    return typeof extracted === 'string' ? extracted.trim() : '';
  }
  return '';
};

// Design System Tokens (8pt Grid & Typography Scale)
const SPACING_TOKENS = {
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
};

const THEMES = {
  startup: {
    nameFont: 'Inter',
    bodyFont: 'Inter',
    nameSize: 22,
    headlineSize: 10.5,
    headerAlign: 'left',
    sectionTitleSize: 11.5,
    bodySize: 9.5,
    bulletPointSize: 10,
    lineHeight: 1.4,
    nameColor: '#0f172a',       // Slate 900
    headlineColor: '#475569',   // Slate 600
    sectionColor: '#0f172a',
    accentColor: '#0ea5e9',     // Sky 500
    sectionBorderWidth: 1.5,
    padding: SPACING_TOKENS.xxl, // 32pt
    bulletType: '•',
    spacing: SPACING_TOKENS.md,  // 12pt
  },
  executive: {
    nameFont: 'Lora',
    bodyFont: 'Inter',
    nameSize: 24,
    headlineSize: 11,
    headerAlign: 'center',
    sectionTitleSize: 12,
    bodySize: 9.5,
    bulletPointSize: 10,
    lineHeight: 1.45,
    nameColor: '#111827',       // Slate 900
    headlineColor: '#4b5563',   // Slate 600
    sectionColor: '#111827',
    accentColor: '#1f2937',     // Dark Charcoal Accent
    sectionBorderWidth: 1,
    padding: SPACING_TOKENS.xxl, // 32pt
    bulletType: '•',
    spacing: SPACING_TOKENS.md,  // 12pt
  },
  engineer: {
    nameFont: 'Roboto Mono',
    bodyFont: 'Roboto',
    nameSize: 22,
    headlineSize: 10.5,
    headerAlign: 'left',
    sectionTitleSize: 12,
    bodySize: 9.5,
    bulletPointSize: 10,
    lineHeight: 1.45,
    nameColor: '#064e3b',       // Emerald 900
    headlineColor: '#374151',   // Gray 700
    sectionColor: '#065f46',    // Emerald 800
    accentColor: '#10b981',     // Emerald 500
    sectionBorderWidth: 2,
    padding: SPACING_TOKENS.xxl, // 32pt
    bulletType: '›',
    spacing: SPACING_TOKENS.md,  // 12pt
  },
};

const legacyMap = {
  modern: 'startup',
  professional: 'executive',
  technical: 'engineer'
};

const createStyles = (themeKey, customPrimaryColor = null) => {
  let resolvedKey = legacyMap[themeKey] || themeKey || 'startup';
  if (!THEMES[resolvedKey]) {
    resolvedKey = 'startup';
  }
  const t = THEMES[resolvedKey];
  const accent = customPrimaryColor || t.accentColor;
  
  return StyleSheet.create({
    page: {
      padding: t.padding,
      backgroundColor: '#ffffff',
      fontFamily: t.bodyFont,
    },
    header: {
      marginBottom: t.spacing * 1.2,
      borderBottomWidth: resolvedKey === 'executive' ? 2 : (resolvedKey === 'startup' ? 0 : 1),
      borderBottomColor: accent,
      borderTopWidth: resolvedKey === 'executive' ? 2 : 0,
      borderTopColor: accent,
      paddingTop: resolvedKey === 'executive' ? 8 : 0,
      paddingBottom: resolvedKey === 'executive' ? 12 : 8,
      borderLeftWidth: resolvedKey === 'engineer' ? 4 : 0,
      borderLeftColor: accent,
      paddingLeft: resolvedKey === 'engineer' ? 12 : 0,
      alignItems: t.headerAlign,
    },
    name: {
      fontSize: t.nameSize,
      fontFamily: t.nameFont,
      fontWeight: 'bold',
      color: resolvedKey === 'engineer' ? accent : t.nameColor,
      marginBottom: 2,
      letterSpacing: resolvedKey === 'startup' ? -0.5 : 0,
    },
    headline: {
      fontSize: t.headlineSize,
      fontFamily: t.bodyFont,
      fontWeight: 'bold',
      color: t.headlineColor,
      marginBottom: 4,
    },
    targetHeadline: {
      fontSize: t.bodySize - 0.5,
      fontFamily: t.bodyFont,
      fontWeight: 'bold',
      color: accent,
      marginBottom: 4,
    },
    contactRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: t.headerAlign === 'center' ? 'center' : 'flex-start',
      gap: 6,
    },
    contactItem: {
      fontSize: t.bodySize - 1.5,
      fontFamily: t.bodyFont,
      color: '#475569',
    },
    section: {
      marginTop: t.spacing,
      marginBottom: t.spacing * 0.5,
    },
    sectionTitle: {
      fontSize: t.sectionTitleSize,
      fontFamily: resolvedKey === 'startup' ? 'Inter' : (resolvedKey === 'executive' ? 'Lora' : 'Roboto Mono'),
      fontWeight: 'bold',
      textTransform: 'uppercase',
      color: resolvedKey === 'engineer' ? accent : t.sectionColor,
      borderBottomWidth: t.sectionBorderWidth,
      borderBottomColor: resolvedKey === 'executive' ? t.sectionColor : accent,
      paddingBottom: 4,
      marginBottom: 8,
      letterSpacing: resolvedKey === 'executive' ? 1 : 0,
    },
    summary: {
      fontSize: t.bodySize,
      fontFamily: t.bodyFont,
      lineHeight: t.lineHeight,
      color: '#334155',
    },
    skRow: {
      fontSize: t.bodySize,
      fontFamily: t.bodyFont,
      lineHeight: t.lineHeight,
      color: '#334155',
      marginBottom: 3.5,
    },
    skLabel: {
      fontFamily: t.bodyFont,
      fontWeight: 'bold',
      color: '#0f172a',
    },
    skItems: {
      fontFamily: t.bodyFont,
      color: '#334155',
    },
    entry: {
      marginBottom: t.spacing,
    },
    entryHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
    entryTitle: {
      fontSize: t.bodySize + 1,
      fontFamily: resolvedKey === 'engineer' ? 'Roboto Mono' : t.bodyFont,
      fontWeight: 'bold',
      color: '#0f172a',
      width: '74%',
    },
    entryDate: {
      fontSize: t.bodySize - 1,
      fontFamily: t.bodyFont,
      color: '#64748b',
      textAlign: 'right',
      width: '26%',
    },
    entrySub: {
      fontSize: t.bodySize,
      fontFamily: t.bodyFont,
      color: '#475569',
      marginTop: 2,
      marginBottom: 4,
    },
    entryDesc: {
      fontSize: t.bodySize,
      fontFamily: t.bodyFont,
      lineHeight: t.lineHeight,
      color: '#334155',
      marginTop: 3,
      marginBottom: 6,
    },
    metaRow: {
      fontSize: t.bodySize - 0.5,
      fontFamily: t.bodyFont,
      lineHeight: 1.45,
      marginTop: 2,
      marginBottom: 3,
    },
    metaLabel: {
      fontFamily: t.bodyFont,
      fontWeight: 'bold',
      color: resolvedKey === 'engineer' ? accent : '#0f172a',
    },
    metaVal: {
      fontFamily: t.bodyFont,
      color: '#475569',
    },
    impactBlock: {
      flexDirection: 'row',
      backgroundColor: resolvedKey === 'engineer' ? '#ecfdf5' : '#f0f9ff',
      borderLeftWidth: 2.5,
      borderLeftColor: accent,
      paddingHorizontal: 8,
      paddingVertical: 5,
      borderRadius: 3,
      marginTop: 6,
      marginBottom: 4,
    },
    impactLabel: {
      fontSize: t.bodySize - 0.5,
      fontFamily: t.bodyFont,
      fontWeight: 'bold',
      color: accent,
    },
    impactText: {
      flex: 1,
      fontSize: t.bodySize - 0.5,
      fontFamily: t.bodyFont,
      color: '#0f172a',
      lineHeight: 1.45,
    },
    bulletRow: {
      flexDirection: 'row',
      marginBottom: 4,
      alignItems: 'flex-start',
    },
    bulletPoint: {
      width: 12,
      fontSize: t.bulletPointSize,
      fontFamily: t.bodyFont,
      color: resolvedKey === 'engineer' ? accent : '#64748b',
      marginTop: 1, // slight offset to align with text
    },
    bulletText: {
      flex: 1,
      fontSize: t.bodySize,
      fontFamily: t.bodyFont,
      lineHeight: t.lineHeight,
      color: '#334155',
    },
  });
};

export const ResumePDFDocument = (props) => {
  const { formData, template = 'startup', sectionsEnabled = {}, primaryColor = null } = props || {};
  const normTemplate = String(template || '').toLowerCase().trim();

  // Branch out to the 3 brand new advanced templates
  if (normTemplate === 'modern_split' || normTemplate === 'modern' || normTemplate === 'moderndualcolumn' || normTemplate === 'modernminimal') {
    return <ModernDualColumnPDF formData={formData} sectionsEnabled={sectionsEnabled} primaryColor={primaryColor} />;
  }
  if (normTemplate === 'data_driven' || normTemplate === 'datadriven' || normTemplate === 'data-driven') {
    return <DataDrivenPDF formData={formData} sectionsEnabled={sectionsEnabled} primaryColor={primaryColor} />;
  }
  if (normTemplate === 'timeline') {
    return <TimelinePDF formData={formData} sectionsEnabled={sectionsEnabled} primaryColor={primaryColor} />;
  }

  // The classic 3 templates logic continues below...
  const p = formData?.personal || {};
  const styles = createStyles(template, primaryColor);
  
  // Resolve bullet
  let resolvedKey = legacyMap[template] || template || 'startup';
  if (!THEMES[resolvedKey]) {
    resolvedKey = 'startup';
  }
  const bulletSymbol = THEMES[resolvedKey].bulletType;

  const breakableUrlStr = (s) => {
    if (!s) return '';
    return String(s).replace(/([/\-_.\?&=])/g, '$1\u200B');
  };

  const ContactLine = () => {
    const items = [
      p.email,
      p.phone,
      p.location,
      p.linkedin ? formatContactLink(p.linkedin, 'linkedin') : null,
      p.github ? formatContactLink(p.github, 'github') : null,
      p.portfolio ? formatContactLink(p.portfolio, 'portfolio') : null,
    ].filter(Boolean);

    return (
      <View style={styles.contactRow}>
        {items.map((item, i) => (
          <Text key={i} style={styles.contactItem}>
            {breakableUrlStr(item)}{i < items.length - 1 ? ' •' : ''}
          </Text>
        ))}
      </View>
    );
  };

  const SummarySection = () => {
    if (!formData?.summary?.trim()) return null;
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle} minPresenceAhead={45}>Professional Summary</Text>
        <Text style={styles.summary}>{cleanStr(formData.summary)}</Text>
      </View>
    );
  };

  const skillsLine = (label, items = []) => {
    const clean = items.filter(Boolean).map(cleanStr);
    if (!clean.length) return null;
    return (
      <Text style={styles.skRow} key={label}>
        <Text style={styles.skLabel}>{label}: </Text>
        <Text style={styles.skItems}>{clean.join(', ')}</Text>
      </Text>
    );
  };

  const SkillsSection = () => {
    const sk = formData?.technicalSkills || {};
    const tl = formData?.tools || {};
    const rows = [
      skillsLine('Core Competencies', formData?.coreCompetencies),
      skillsLine('Testing', sk.testing),
      skillsLine('Automation', sk.automation),
      skillsLine('Programming', sk.programming),
      skillsLine('Databases', sk.databases),
      skillsLine('Cloud Platforms', sk.cloud),
      skillsLine('AI & ML Tools', sk.aiTools),
      skillsLine('Test Management', tl.testManagement),
      skillsLine('CI/CD', tl.cicd),
      skillsLine('Monitoring', tl.monitoring),
    ].filter(Boolean);

    if (!rows.length) return null;
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle} minPresenceAhead={45}>Skills & Expertise</Text>
        {rows}
      </View>
    );
  };

  const entryBlock = (title, subtitle, date, description = '', bullets = [], key) => {
    const hasDate = Boolean(cleanStr(date));
    return (
      <View style={styles.entry} key={key}>
        {hasDate ? (
          <View style={styles.entryHeader}>
            <Text style={styles.entryTitle}>{cleanStr(title)}</Text>
            <Text style={styles.entryDate}>{cleanStr(date)}</Text>
          </View>
        ) : (
          <View style={{ marginBottom: 2 }}>
            <Text style={[styles.entryTitle, { width: '100%' }]}>{cleanStr(title)}</Text>
          </View>
        )}
        {cleanStr(subtitle) ? <Text style={styles.entrySub}>{cleanStr(subtitle)}</Text> : null}
        {cleanStr(description) ? <Text style={styles.entryDesc}>{cleanStr(description)}</Text> : null}
        {bullets.filter(Boolean).map((b, i) => {
          const txt = typeof b === 'object' ? b.text : b;
          if (!txt?.trim()) return null;
          return (
            <View style={styles.bulletRow} key={i} wrap={false}>
              <Text style={styles.bulletPoint}>{bulletSymbol}</Text>
              <Text style={styles.bulletText}>{cleanStr(txt)}</Text>
            </View>
          );
        })}
      </View>
    );
  };

  const ExperienceSection = () => {
    const entries = (formData?.experience || []).filter(e => e.company?.trim() || e.role?.trim());
    if (!entries.length) return null;
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle} minPresenceAhead={45}>Work Experience</Text>
        {entries.map((e, i) => {
          const techClean = (e.technologies || '').trim();
          const achievements = e.achievements || [];
          return (
            <View style={styles.entry} key={i}>
              <View wrap={false}>
                <View style={styles.entryHeader}>
                  <Text style={styles.entryTitle}>{cleanStr(e.role)}</Text>
                  <Text style={styles.entryDate}>{cleanStr(e.duration)}</Text>
                </View>
                <Text style={styles.entrySub}>
                  {[e.company, e.location].filter(Boolean).map(cleanStr).join(', ')}
                </Text>
                {techClean ? (
                  <Text style={styles.metaRow}>
                    <Text style={styles.metaLabel}>Technologies: </Text>
                    <Text style={styles.metaVal}>{cleanStr(techClean)}</Text>
                  </Text>
                ) : null}
              </View>
              {achievements.map((b, bi) => {
                const txt = typeof b === 'object' ? b.text : b;
                if (!txt?.trim()) return null;
                return (
                  <View style={styles.bulletRow} key={bi} wrap={false}>
                    <Text style={styles.bulletPoint}>{bulletSymbol}</Text>
                    <Text style={styles.bulletText}>{cleanStr(txt)}</Text>
                  </View>
                );
              })}
              {e.impact ? (
                <View style={styles.impactBlock} wrap={false}>
                  <Text style={styles.impactLabel}>Key Impact: </Text>
                  <Text style={styles.impactText}>{cleanStr(e.impact)}</Text>
                </View>
              ) : null}
            </View>
          );
        })}
      </View>
    );
  };

  const ProjectsSection = () => {
    const entries = (formData?.projects || []).filter(p => p.title?.trim());
    if (!entries.length) return null;
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle} minPresenceAhead={45}>Projects</Text>
        {entries.map((p, i) => {
          const { cleanTitle, domainClean, techClean } = formatProjectMetadata(p);
          const respBullets = getResponsibilityBullets(p).filter(b => cleanStr(b).length > 0);
          return (
            <View style={styles.entry} key={i}>
              <View wrap={false}>
                <View style={styles.entryHeader}>
                  <Text style={styles.entryTitle}>{cleanTitle}</Text>
                </View>
                {domainClean ? (
                  <Text style={styles.metaRow}>
                    <Text style={styles.metaLabel}>Domain: </Text>
                    <Text style={styles.metaVal}>{cleanStr(domainClean)}</Text>
                  </Text>
                ) : null}
                {techClean ? (
                  <Text style={styles.metaRow}>
                    <Text style={styles.metaLabel}>Technologies: </Text>
                    <Text style={styles.metaVal}>{cleanStr(techClean)}</Text>
                  </Text>
                ) : null}
                {p.description ? (
                  <Text style={styles.entryDesc}>{cleanStr(p.description)}</Text>
                ) : null}
              </View>
              {respBullets.map((respTxt, rIdx) => (
                <View style={styles.bulletRow} key={rIdx} wrap={false}>
                  <Text style={styles.bulletPoint}>{bulletSymbol}</Text>
                  <Text style={styles.bulletText}>{cleanStr(respTxt)}</Text>
                </View>
              ))}
              {p.impact ? (
                <View style={styles.impactBlock} wrap={false}>
                  <Text style={styles.impactLabel}>Key Impact: </Text>
                  <Text style={styles.impactText}>{cleanStr(p.impact)}</Text>
                </View>
              ) : null}
            </View>
          );
        })}
      </View>
    );
  };

  const EducationSection = () => {
    const entries = (formData?.education || []).filter(e => e.degree?.trim() || e.university?.trim());
    if (!entries.length) return null;
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle} minPresenceAhead={45}>Education</Text>
        {entries.map((e, i) => 
          entryBlock(e.degree, [e.university, e.gpa ? `GPA: ${e.gpa}` : ''].filter(Boolean).join(' — '), e.year, '', [], i)
        )}
      </View>
    );
  };

  const CertificationsSection = () => {
    const entries = (formData?.certifications || []).filter(c => c.name?.trim());
    if (!entries.length) return null;
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle} minPresenceAhead={45}>Certifications</Text>
        {entries.map((c, i) => 
          entryBlock(c.name, [c.organization, c.credentialId ? `ID: ${c.credentialId}` : ''].filter(Boolean).join(' — '), c.year, '', [], i)
        )}
      </View>
    );
  };

  const OptionalSections = () => {
    const blocks = [];

    // Key Achievements
    const achEntries = (formData?.achievements || []).filter(a => a.title?.trim());
    if (achEntries.length > 0) {
      blocks.push(
        <View style={styles.section} key="achievements">
          <Text style={styles.sectionTitle} minPresenceAhead={45}>Key Achievements</Text>
          {achEntries.map((a, i) => entryBlock(a.title, [a.organization, a.year].filter(Boolean).map(cleanStr).join(', '), '', a.description, [], i))}
        </View>
      );
    }

    // Awards & Recognition
    const awardsEntries = (formData?.awards || []).filter(a => a.title?.trim());
    if (awardsEntries.length > 0) {
      blocks.push(
        <View style={styles.section} key="awards">
          <Text style={styles.sectionTitle} minPresenceAhead={45}>Awards & Recognition</Text>
          {awardsEntries.map((a, i) => entryBlock(a.title, [a.organization, a.year].filter(Boolean).map(cleanStr).join(' • '), '', a.description, [], i))}
        </View>
      );
    }

    // Volunteer Work
    const volEntries = (formData?.volunteer || []).filter(v => v.organization?.trim() || v.role?.trim());
    if (volEntries.length > 0) {
      blocks.push(
        <View style={styles.section} key="volunteer">
          <Text style={styles.sectionTitle} minPresenceAhead={45}>Volunteer Work</Text>
          {volEntries.map((v, i) => entryBlock(v.role || v.organization, [v.organization, v.duration].filter(Boolean).map(cleanStr).join(' — '), '', v.description, [], i))}
        </View>
      );
    }

    // Publications & Talks
    const pubEntries = (formData?.publications || []).filter(p => p.title?.trim());
    if (pubEntries.length > 0) {
      blocks.push(
        <View style={styles.section} key="pubs">
          <Text style={styles.sectionTitle} minPresenceAhead={45}>Publications & Talks</Text>
          {pubEntries.map((p, i) => entryBlock(p.title, [p.publication, p.type, p.date].filter(Boolean).map(cleanStr).join(' — '), '', p.url ? `URL: ${breakableUrlStr(p.url)}` : '', [], i))}
        </View>
      );
    }

    // Open Source Contributions
    const osEntries = (formData?.openSource || []).filter(o => o.project?.trim());
    if (osEntries.length > 0) {
      blocks.push(
        <View style={styles.section} key="opensource">
          <Text style={styles.sectionTitle} minPresenceAhead={45}>Open Source Contributions</Text>
          {osEntries.map((o, i) => entryBlock(o.project, [o.role, o.technologies ? `Tech: ${o.technologies}` : '', o.stars ? `★ ${o.stars}` : ''].filter(Boolean).map(cleanStr).join(' • '), '', [o.description, o.url ? `Repository: ${breakableUrlStr(o.url)}` : ''].filter(Boolean).join(' — '), [], i))}
        </View>
      );
    }

    // Professional Memberships
    const memEntries = (formData?.memberships || []).filter(m => m.organization?.trim());
    if (memEntries.length > 0) {
      blocks.push(
        <View style={styles.section} key="memberships">
          <Text style={styles.sectionTitle} minPresenceAhead={45}>Professional Memberships</Text>
          {memEntries.map((m, i) => entryBlock(m.organization, [m.role, m.level, m.since ? `Since ${m.since}` : ''].filter(Boolean).map(cleanStr).join(' • '), '', '', [], i))}
        </View>
      );
    }

    // Portfolio & Case Studies
    const portEntries = (formData?.portfolio || []).filter(p => p.title?.trim());
    if (portEntries.length > 0) {
      blocks.push(
        <View style={styles.section} key="portfolio">
          <Text style={styles.sectionTitle} minPresenceAhead={45}>Portfolio & Case Studies</Text>
          {portEntries.map((p, i) => entryBlock(p.title, [p.type, p.technologies ? `Tech: ${p.technologies}` : ''].filter(Boolean).map(cleanStr).join(' • '), '', [p.description, p.url ? `Link: ${breakableUrlStr(p.url)}` : ''].filter(Boolean).join(' — '), [], i))}
        </View>
      );
    }

    // Patents & Innovations
    const patEntries = (formData?.patents || []).filter(p => p.title?.trim());
    if (patEntries.length > 0) {
      blocks.push(
        <View style={styles.section} key="patents">
          <Text style={styles.sectionTitle} minPresenceAhead={45}>Patents & Innovations</Text>
          {patEntries.map((p, i) => entryBlock(p.title, [p.patentNumber ? `Patent #${p.patentNumber}` : '', p.status ? `Status: ${p.status}` : '', p.date].filter(Boolean).map(cleanStr).join(' • '), '', p.description, [], i))}
        </View>
      );
    }

    // Languages
    const langEntries = (formData?.languages || []).filter(l => l.language?.trim());
    if (langEntries.length > 0) {
      const line = langEntries.map(l => `${cleanStr(l.language)} (${cleanStr(l.proficiency || 'Professional')})`).join(', ');
      blocks.push(
        <View style={styles.section} key="langs">
          <Text style={styles.sectionTitle} minPresenceAhead={45}>Languages</Text>
          <View style={styles.skRow}><Text style={styles.skItems}>{line}</Text></View>
        </View>
      );
    }

    return blocks.length > 0 ? <>{blocks}</> : null;
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.name}>{cleanStr(p.name || 'Your Name')}</Text>
          <Text style={styles.headline}>{cleanStr(p.jobTitle || 'QA Engineer')}</Text>
          {cleanStr(p?.targetHeadline) ? <Text style={styles.targetHeadline}>{cleanStr(p.targetHeadline)}</Text> : null}
          <ContactLine />
        </View>

        <SummarySection />
        <SkillsSection />
        <ExperienceSection />
        <ProjectsSection />
        <CertificationsSection />
        <EducationSection />
        <OptionalSections />
      </Page>
    </Document>
  );
};
